import React, { useState, useEffect } from 'react';
import { jobInquiriesApi, jobPositionTypesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX, HiOutlineDocumentText, HiOutlineTrash } from 'react-icons/hi';
import { formatValidationErrors } from '../../utils/errorHandler';

const JobInquiryForm = ({ inquiry, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumePreview, setResumePreview] = useState(null);
  const [positionTypes, setPositionTypes] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    contact_number: '',
    email: '',
    position_type: '',
    salary_requirements: '',
    salary_currency: 'INR',
    experience_years: '',
    qualifications: '',
    notes: '',
    status: 'new'
  });

  useEffect(() => {
    if (inquiry) {
      setFormData({
        full_name: inquiry.full_name || '',
        contact_number: inquiry.contact_number || '',
        email: inquiry.email || '',
        position_type: inquiry.position_type || '',
        salary_requirements: inquiry.salary_requirements || '',
        salary_currency: inquiry.salary_currency || 'INR',
        experience_years: inquiry.experience_years || '',
        qualifications: inquiry.qualifications || '',
        notes: inquiry.notes || '',
        status: inquiry.status || 'new'
      });
      
      // If inquiry has existing resume, show it
      if (inquiry.resume_file_name) {
        setResumePreview({
          name: inquiry.resume_file_name,
          url: inquiry.resume_url || null,
          isExisting: true
        });
      }
    }
  }, [inquiry]);

  useEffect(() => {
    let mounted = true;
    const loadPositionTypes = async () => {
      try {
        const res = await jobPositionTypesApi.getActive();
        const items = res.data?.data || [];
        if (!mounted) return;
        setPositionTypes(items);
        setFormData((prev) => {
          if (inquiry?.position_type) return prev;
          if (prev.position_type) return prev;
          return { ...prev, position_type: items[0]?.name || '' };
        });
      } catch {
        if (mounted) {
          setPositionTypes([]);
        }
      }
    };
    loadPositionTypes();
    return () => {
      mounted = false;
    };
  }, [inquiry?.position_type]);

  const availablePositionTypes = React.useMemo(() => {
    if (!inquiry?.position_type) return positionTypes;
    const exists = positionTypes.some(
      (t) => String(t.name).toLowerCase() === String(inquiry.position_type).toLowerCase()
    );
    if (exists) return positionTypes;
    return [
      ...positionTypes,
      { id: `legacy-${inquiry.position_type}`, name: inquiry.position_type, is_active: false },
    ];
  }, [positionTypes, inquiry?.position_type]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, DOC, and DOCX files are allowed');
      e.target.value = '';
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      e.target.value = '';
      return;
    }

    setResumeFile(file);
    setResumePreview({
      name: file.name,
      size: file.size,
      type: file.type,
      isExisting: false
    });
  };

  const removeResume = () => {
    setResumeFile(null);
    setResumePreview(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.contact_number) {
      toast.error('Full name and contact number are required');
      return;
    }

    setLoading(true);

    try {
      if (inquiry) {
        await jobInquiriesApi.update(inquiry.id, formData, resumeFile);
        toast.success('Job inquiry updated successfully');
      } else {
        await jobInquiriesApi.create(formData, resumeFile);
        toast.success('Job inquiry created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">
            {inquiry ? 'Edit Job Inquiry' : 'New Job Inquiry'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  required
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position Type
                </label>
                <select
                  name="position_type"
                  value={formData.position_type}
                  onChange={handleChange}
                  className="input w-full"
                >
                  {availablePositionTypes.length === 0 && (
                    <option value="">No position types configured</option>
                  )}
                  {availablePositionTypes.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Professional Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Professional Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience (Years)
                </label>
                <input
                  type="number"
                  name="experience_years"
                  value={formData.experience_years}
                  onChange={handleChange}
                  min="0"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary Requirements
                </label>
                <div className="flex gap-2">
                  <select
                    name="salary_currency"
                    value={formData.salary_currency}
                    onChange={handleChange}
                    className="input w-24"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                  <input
                    type="number"
                    name="salary_requirements"
                    value={formData.salary_requirements}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Amount"
                    className="input flex-1"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Qualifications
                </label>
                <textarea
                  name="qualifications"
                  value={formData.qualifications}
                  onChange={handleChange}
                  rows={3}
                  className="input w-full"
                  placeholder="Enter qualifications, certifications, etc."
                />
              </div>
            </div>
          </div>

          {/* Resume Upload */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Resume</h4>
            {resumePreview ? (
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <HiOutlineDocumentText className="w-8 h-8 text-primary-600" />
                    <div>
                      <p className="font-medium text-gray-900">{resumePreview.name}</p>
                      {resumePreview.size && (
                        <p className="text-sm text-gray-500">{formatFileSize(resumePreview.size)}</p>
                      )}
                      {resumePreview.isExisting && resumePreview.url && (
                        <a
                          href={resumePreview.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:underline"
                        >
                          View existing resume
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeResume}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <HiOutlineTrash className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Resume (PDF, DOC, DOCX - Max 10MB)
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: PDF, DOC, DOCX. Maximum file size: 10MB
                </p>
              </div>
            )}
          </div>

          {/* Additional Notes */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Additional Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  className="input w-full"
                  placeholder="Any additional notes or comments..."
                />
              </div>
              {inquiry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input w-full"
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="rejected">Rejected</option>
                    <option value="hired">Hired</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : inquiry ? 'Update Inquiry' : 'Create Inquiry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobInquiryForm;

